import { Button, makeStyles, mergeClasses, Tooltip, tokens } from '@fluentui/react-components'
import { CheckmarkRegular, FolderFilled } from '@fluentui/react-icons'
import { forwardRef, type ReactNode } from 'react'

import type { WorkspaceItem } from '../../../../shared/models/WorkspaceItem'
import { formatDate } from '../../../../utils/dateUtil'

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, min(100%, 160px))',
    gap: tokens.spacingHorizontalL,
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  item: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    height: '180px',
    boxSizing: 'border-box',
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: 'transparent',
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':focus-within': { backgroundColor: tokens.colorSubtleBackgroundHover },
    ':hover [data-workspace-grid-actions]': { opacity: 1 },
    ':focus-within [data-workspace-grid-actions]': { opacity: 1 },
  },
  selected: {
    backgroundColor: tokens.colorBrandBackground2,
    ':hover': { backgroundColor: tokens.colorBrandBackground2Hover },
    ':focus-within': { backgroundColor: tokens.colorBrandBackground2Hover },
  },
  open: {
    display: 'grid',
    gridTemplateRows: '112px minmax(0, 1fr)',
    rowGap: tokens.spacingVerticalS,
    width: '100%',
    height: '100%',
    minWidth: 0,
    padding: tokens.spacingHorizontalS,
    border: 0,
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusLarge,
    fontWeight: tokens.fontWeightRegular,
    ':hover': { backgroundColor: 'transparent' },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    minHeight: 0,
    boxSizing: 'border-box',
    padding: tokens.spacingHorizontalS,
    overflow: 'hidden',
    backgroundColor: 'transparent',
    '& > span': { backgroundColor: 'transparent' },
    fontSize: '40px',
    color: tokens.colorBrandForeground1,
  },
  folderIcon: {
    backgroundColor: 'transparent',
    '& svg': { fontSize: '96px' },
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    minWidth: 0,
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
  },
  name: {
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    fontWeight: tokens.fontWeightSemibold,
  },
  metadata: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  commands: {
    position: 'absolute',
    top: tokens.spacingVerticalS,
    left: tokens.spacingHorizontalS,
    right: tokens.spacingHorizontalS,
    pointerEvents: 'none',
    opacity: 0,
    '@media (hover: none), (pointer: coarse)': { opacity: 1 },
    '& button': {
      pointerEvents: 'auto',
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
  indicator: {
    position: 'absolute',
    top: tokens.spacingVerticalS,
    left: tokens.spacingHorizontalS,
    color: tokens.colorBrandForeground1,
  },
})

export function WorkspaceItemsGrid({
  children,
  label = 'Workspace items',
}: {
  children: ReactNode
  label?: string
}) {
  const styles = useStyles()
  return (
    <ul className={styles.grid} aria-label={label}>
      {children}
    </ul>
  )
}

export const WorkspaceItemsGridTile = forwardRef<
  HTMLLIElement,
  {
    item: WorkspaceItem
    icon: ReactNode
    tooltip?: ReactNode
    metadata?: ReactNode
    selected?: boolean
    className?: string
    children?: ReactNode
    onOpen: (item: WorkspaceItem) => Promise<void>
  }
>(function WorkspaceItemsGridTile(
  { item, icon, tooltip, metadata, selected, className, children, onOpen },
  ref,
) {
  const styles = useStyles()

  return (
    <li
      ref={ref}
      className={mergeClasses(
        styles.item,
        selected && styles.selected,
        className,
      )}
    >
      <Tooltip content={<div>{tooltip ?? item.name}</div>} relationship="description">
        <Button
          data-workspace-item-open
          appearance="subtle"
          className={styles.open}
          aria-label={`${selected === undefined ? 'Open' : 'Select'} ${item.name}`}
          aria-pressed={selected}
          onClick={() => void onOpen(item)}
        >
          <span className={mergeClasses(styles.icon, item.type === 'folder' && styles.folderIcon)}>
            {item.type === 'folder' ? <FolderFilled /> : icon}
          </span>
          <span className={styles.details}>
            <span className={styles.name}>{item.name}</span>
            <span className={styles.metadata}>
              {metadata ?? formatDate(item.updatedAt)}
            </span>
          </span>
          {selected && <CheckmarkRegular aria-hidden="true" className={styles.indicator} />}
        </Button>
      </Tooltip>
      {children && (
        <div className={styles.commands} data-workspace-grid-actions>
          {children}
        </div>
      )}
    </li>
  )
})
